import { AccessLevel } from '@/modules/common/enums/access-levels.enum'
import GtrSuper from '@/modules/common/components/mixins/gtr-super.mixin'
import GtrEventBookmarks from '@/modules/common/components/ui-core/gtr-event-bookmarks/gtr-event-bookmarks.vue'
import FloatingPrintButton from '@/modules/common/components/ui-core/floating-print-button/floating-print-button.vue'
import { Layout } from '@/modules/common/enums/layouts.enum'
import { Component, Watch } from 'vue-property-decorator'
import { mapGetters, mapState } from 'vuex'
import Container from 'typedi'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import { canReadBadgeSettings } from '@/modules/common/utils/badge-settings-permission.util'

@Component({
  name: 'GtrEventLayout',
  computed: {
    ...mapState('company', ['company']),
    ...mapState('event', ['event', 'eventModules', 'eventBookmarks']),
    ...mapState('module', ['eventOverview', 'gtrModules', 'activatedEventModules', 'support']),
    ...mapState('common', ['noticeDisplayed']),
    ...mapState('leads', ['changelog']),
    ...mapGetters('media', ['defaultFonts', 'fontList'])
  },
  components: {
    'gtr-event-bookmarks': GtrEventBookmarks,
    'floating-print-button': FloatingPrintButton
  }
})
export default class GtrEventLayout extends GtrSuper {
  eventModules!: any;
  eventOverview!: any;
  gtrModules!: any;
  support!: any;
  noticeDisplayed!: boolean;
  event!: Record<string, any>;
  defaultFonts!: Array<Record<string, any>>;
  fontList!: Array<Record<string, any>>;
  AccessLevel = AccessLevel

  get menuItems () {
    return [
      this.eventOverview,
      ...this.gtrModules,
      this.support
    ]
  }

  get isMobile (): boolean {
    return this.$vuetify.breakpoint.xsOnly
  }

  data () {
    return {
      miniVariant: false,
      legacyNavOpen: false,
      isTrackActivated: false,
      isPromoteActivated: false,
      mountComplete: false
    }
  }

  private fetchLeadsChangelog () {
    try {
      const changelogData = { type: 'orders_settings', subtype: 'default', event_uuid: this.$route.params.event_uuid }
      this.$store.dispatch('leads/getChangelog', changelogData).catch(error => Container.get(ErrorHandlerService).error(error))
    } catch (error: any) {
      if (error.status !== 404) {
        Container.get(ErrorHandlerService).error(error)
      }
    }
  }

  get mostRecentLeadsChangelogUpdatedAtTimestamp () {
    return (this as any)?.changelog?.data?.[0]?.updated_at ?? null
  }

  /**
   * When the layout for events loads it should fetch the event and company info
   * As well as the event modules for the current event.
   */
  async mounted () {
    try {
      if (!this.$store.state.security.currentUser) {
        await this.$store.dispatch('security/getCurrentUser')
      }
      this.fetchLeadsChangelog()
      await this.loadEventAndCompanyInfo()
      await this.fetchEventModules()
      await this.fetchBadgeSettings()
      this.$data.mountComplete = true
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  /**
   * The Event layout is cached because of keep-alive.
   * This hook will refetch the modules everytime the layout is reactivated.
   */
  async activated () {
    try {
      await this.fetchEventModules()
      await this.fetchBadgeSettings()
    } catch (err) {
      Container.get(ErrorHandlerService).error(err)
    }
  }

  @Watch('eventModules')
  async onEventModulesChange (payload: any) {
    if (payload.SESSION_TRACKING.enabled) this.$data.isTrackActivated = true
    if (!payload.SESSION_TRACKING.enabled) this.$data.isTrackActivated = false
    if (payload.PROMOTE.enabled) this.$data.isPromoteActivated = true
    if (!payload.PROMOTE.enabled) this.$data.isPromoteActivated = false
    const { REGISTRATION, LEADS } = this.eventModules
    if (REGISTRATION?.enabled || LEADS?.enabled) {
      this.$store.commit('module/SET_SUBMODULE_VISIBILITY', { routeName: 'level-two.event.payments', active: true })
    } else {
      this.$store.commit('module/SET_SUBMODULE_VISIBILITY', { routeName: 'level-two.event.payments', active: false })
    }
  }

  @Watch('$route', { immediate: true })
  async onRouteChange () {
    if (this.$route.params.uuid && this.$route.params.event_uuid) {
      this.$data.menuItems = this.makeSideBarMenu(this.$route.params.uuid, Layout.EVENT, this.$route.params.event_uuid)
      if (this.$store.state.event.eventAllContent) {
        if (this.$store.state.event.eventAllContent.event.uuid !== this.$route.params.event_uuid) {
          await this.loadEventAndCompanyInfo()
        }
      }
    }
  }

  get regUrl () {
    const event = this.$store.state.event.event
    if (event?.event_identifier) {
      return `${process.env.VUE_APP_REG_URL}/${event.event_identifier}`
    }
  }

  get ordersUrl () {
    const event = this.$store.state.event.event
    if (event?.event_identifier) {
      return `${process.env.VUE_APP_REG_URL}/${event.event_identifier}/leads`
    }
  }

  navigateTo (route: any) {
    this.$router.push(route).catch(error => Container.get(ErrorHandlerService).error(error))
  }

  private async loadEventAndCompanyInfo (): Promise<void> {
    try {
      await this.$store.dispatch('common/showLoader', { value: true })
      await this.$store.dispatch('company/fetchCompany', this.$route.params.uuid)
      await this.$store.dispatch('event/fetchEvent', this.$route.params.event_uuid)
      await this.$store.dispatch('event/getEventAllContent', this.$route.params.event_uuid)
      await this.fetchFontList()
      this.$bus.$emit('gtr.event.content_all')
      await this.$store.dispatch('common/hideLoader')
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  private async fetchEventModules (): Promise<void> {
    try {
      const { event_uuid } = this.$route.params
      await this.$store.dispatch('module/fetchEventModules', event_uuid)
    } catch (err) {
      Container.get(ErrorHandlerService).error(err)
    }
  }

  private async fetchBadgeSettings (): Promise<void> {
    if (!canReadBadgeSettings((key: string) => this.$hasPermission(key))) {
      return
    }
    try {
      const { event_uuid } = this.$route.params
      await this.$store.dispatch('badges/getSettings', { event_uuid })
    } catch (err) {
      Container.get(ErrorHandlerService).error(err)
    }
  }

  async fetchFontList () {
    const { uuid: companyUuid } = this.$route.params
    await this.$store.dispatch('media/getFontStores', companyUuid)
  }
}
